import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useCustomerAuth, customerApi } from "../lib/customerAuth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  CalendarBlank, Ticket, Receipt, Crown, ChatCircleDots, User as UserIcon, Sparkle,
  SignOut, DownloadSimple, MapPin, ShieldCheck, Bell, House, CarSimple, Wallet,
  ArrowRight, Plus, X as XIcon, Star, Storefront, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatErr, BACKEND_URL } from "../lib/api";
import CustomerChatPanel from "./components/CustomerChatPanel";
import ThemeSwitch from "./components/ThemeSwitch";
import { PushNotificationsToggle } from "../components/PushNotificationsToggle";
import NotificationInboxDropdown from "../components/notifications/NotificationInboxDropdown";
import { NotificationPreferencesCard } from "../components/notifications/NotificationPreferencesCard";
import ActiveSessionsCard from "../components/security/ActiveSessionsCard";

const fmtEur = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const TABS = [
  { key: "home",         label: "Inicio",       icon: House },
  { key: "appointments", label: "Mis citas",    icon: CalendarBlank },
  { key: "vehicles",     label: "Vehículos",    icon: CarSimple },
  { key: "wallet",       label: "Mi cartera",   icon: Wallet },
  { key: "chats",        label: "Chats",        icon: ChatCircleDots },
  { key: "profile",      label: "Mis datos",    icon: UserIcon },
  { key: "preferences",  label: "Preferencias", icon: Bell },
  { key: "security",     label: "Seguridad",    icon: ShieldCheck },
];

export default function CustomerAccount() {
  const { account, loading, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = (() => {
    const t = new URLSearchParams(location.search).get("tab");
    return TABS.find(x => x.key === t) ? t : "home";
  })();
  const [tab, setTab] = useState(initialTab);

  if (loading) return <div className="min-h-screen grid place-items-center text-gray-500">Cargando…</div>;
  if (!account) return <Navigate to="/cliente/login" replace />;

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="customer-account-page">
      {/* Topbar */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkle size={22} weight="duotone" className="text-[var(--ag-gold)]" />
            <span className="font-display font-black tracking-tighter text-xl text-[#1A1A1A]">Mi cuenta</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)] ml-2">AutoGlow Ecosystem</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeSwitch />
            <NotificationInboxDropdown />
            <PushNotificationsToggle role="customer" className="rounded-sm border-gray-200" label="" />
            <Button variant="outline" size="sm" onClick={()=>navigate("/marketplace")}
              className="rounded-sm border-gray-200" data-testid="back-to-marketplace">
              Marketplace
            </Button>
            <Button variant="outline" size="sm" onClick={()=>{ logout(); navigate("/cliente/login"); }}
              className="rounded-sm border-red-200 text-red-600 hover:bg-red-50" data-testid="customer-logout">
              <SignOut size={12} className="mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Hero personalizado · Jun 2026 (B-mockup micuenta) */}
        <CustomerHero account={account} />

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto border-b mb-6 -mx-1 border-gray-200 dark:border-white/10" data-testid="customer-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={()=>setTab(key)}
              className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                tab===key
                  ? "border-[#D4AF37] text-[#1A1A1A] dark:border-[#F5C76A] dark:text-white"
                  : "border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
              data-testid={`tab-${key}`}>
              <Icon size={14} className="inline mr-1.5" weight={tab===key ? "fill" : "duotone"} /> {label}
            </button>
          ))}
        </div>

        {tab === "home"          && <HomeTab account={account} setTab={setTab} />}
        {tab === "appointments"  && <AppointmentsTab />}
        {tab === "vehicles"      && <VehiclesTab />}
        {tab === "wallet"        && <WalletTab />}
        {tab === "chats"         && <CustomerChatPanel />}
        {tab === "profile"       && <ProfileTab />}
        {tab === "preferences"   && <PreferencesTab />}
        {tab === "security"      && <SecurityTab />}
      </div>
    </div>
  );
}

// ---- Hero personalizado ----
function CustomerHero({ account }) {
  const [stats, setStats] = useState({ proximas: 0, completadas: 0, canceladas: 0, total_spent: 0 });
  useEffect(() => {
    customerApi.get("/customer/appointments").then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      const now = new Date();
      let prox = 0, done = 0, canc = 0, spent = 0;
      for (const a of list) {
        const st = (a.status || "").toLowerCase();
        if (st === "cancelado") canc++;
        else if (st === "pagado" || st === "completado") { done++; spent += Number(a.total_price || 0); }
        else {
          // Pendientes / agendadas
          try {
            const d = new Date(a.date);
            if (d >= now) prox++; else done++;
          } catch { prox++; }
        }
      }
      setStats({ proximas: prox, completadas: done, canceladas: canc, total_spent: spent });
    }).catch(() => {});
  }, []);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 6) return "Buenas noches";
    if (h < 13) return "Buenos días";
    if (h < 20) return "Buenas tardes";
    return "Buenas noches";
  })();
  const firstName = (account?.name || "").split(" ")[0] || "Cliente";

  return (
    <div className="mb-6 grid sm:grid-cols-[1fr_auto] gap-5 items-center rounded-2xl p-5 sm:p-6 shadow-sm border transition-colors
      bg-gradient-to-br from-white to-[#F0EEE3] border-gray-100
      dark:from-[#1A1D26] dark:to-[#0F1117] dark:border-white/10 dark:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.6)]" data-testid="customer-hero">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8860B] dark:from-[#F5C76A] dark:to-[#D4AF37] grid place-items-center text-white font-display font-black text-xl shadow-md shrink-0">
          {firstName.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#B8860B] dark:text-[#F5C76A]">{greeting}</div>
          <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tighter truncate text-[#1A1A1A] dark:text-white" data-testid="customer-hero-name">
            Hola, {firstName} 👋
          </h1>
          <p className="text-xs mt-0.5 truncate text-gray-500 dark:text-gray-400">{account?.email}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3 min-w-[260px]" data-testid="customer-hero-counters">
        <Counter value={stats.proximas} label="Próximas" color="#B8860B" darkColor="#F5C76A" />
        <Counter value={stats.completadas} label="Completadas" color="#16a34a" darkColor="#4ADE80" />
        <Counter value={stats.canceladas} label="Canceladas" color="#dc2626" darkColor="#F87171" />
      </div>
    </div>
  );
}

function Counter({ value, label, color, darkColor }) {
  return (
    <div className="text-center rounded-xl py-2.5 px-2 border bg-white border-gray-100 dark:bg-[#0F1117] dark:border-white/10">
      <div className="font-display font-black text-xl sm:text-2xl tabular-nums leading-none dark:hidden" style={{ color }}>{value}</div>
      <div className="font-display font-black text-xl sm:text-2xl tabular-nums leading-none hidden dark:block" style={{ color: darkColor }}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider font-bold mt-1 text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
}

// ---- Tabs ----

function AppointmentsTab() {
  const [items, setItems] = useState(null);
  useEffect(() => { customerApi.get("/customer/appointments").then(({ data }) => setItems(data)); }, []);
  if (!items) return <Loading />;
  if (!items.length) return <Empty msg="Aún no tienes citas. Visita el Marketplace para reservar." />;
  return (
    <div className="space-y-3" data-testid="appointments-tab">
      {items.map((a) => (
        <div key={a.id} className="bg-white border-l-4 border-gray-200 rounded-sm p-4 flex flex-wrap gap-3 items-center"
          style={{ borderLeftColor: a.organization_brand_color || "#0033A0" }}
          data-testid={`my-appt-${a.id}`}>
          {a.organization_logo
            ? <img src={a.organization_logo} alt="" className="w-10 h-10 rounded-sm object-cover" />
            : <div className="w-10 h-10 rounded-sm bg-gray-100 grid place-items-center"><Sparkle size={16} className="text-[var(--ag-text-muted)]" /></div>}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-[#1A1A1A]">{a.wash_type}</div>
            <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-2">
              <span>{a.organization_name}</span>
              {a.organization_city && <span>· {a.organization_city}</span>}
              <span>· {a.license_plate}</span>
            </div>
            {a.pickup_service && a.pickup_address && (
              <div className="text-[10px] mt-1 inline-flex items-center gap-1 bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded font-semibold">
                <MapPin size={9} /> Recogida: {a.pickup_address}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500 tabular-nums">{fmtDate(a.date)}</div>
            <div className="text-[10px] uppercase tracking-wider font-semibold mt-0.5"
              style={{ color: a.status === "Pagado" ? "#16a34a" : a.status === "Cancelado" ? "#dc2626" : "#0033A0" }}>
              {a.status}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function VouchersTab() {
  const [items, setItems] = useState(null);
  useEffect(() => { customerApi.get("/customer/vouchers").then(({ data }) => setItems(data)); }, []);
  if (!items) return <Loading />;
  if (!items.length) return <Empty msg="Sin bonos activos. Pregunta a tu taller por planes de fidelidad." />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="vouchers-tab">
      {items.map((v) => (
        <div key={v.id} className="bg-white border border-gray-200 rounded-md p-4 relative overflow-hidden"
          data-testid={`my-voucher-${v.id}`}>
          <div className="absolute top-0 right-0 w-1.5 h-full"
            style={{ background: v.organization_brand_color || "#0033A0" }} />
          <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)]">{v.organization_name}</div>
          <div className="font-display font-bold text-lg mt-1 text-[#1A1A1A]">{v.voucher_name || "Bono"}</div>
          <div className="text-3xl font-black tabular-nums mt-2"
            style={{ color: v.organization_brand_color || "#0033A0" }}>
            {v.washes_remaining}
            <span className="text-xs ml-1 text-gray-500 font-normal">restantes</span>
          </div>
          <div className="text-[10px] text-gray-500 mt-1">Caduca: {v.expires_at?.slice(0,10) || "—"}</div>
        </div>
      ))}
    </div>
  );
}

function InvoicesTab() {
  const [items, setItems] = useState(null);
  useEffect(() => { customerApi.get("/customer/invoices").then(({ data }) => setItems(data)); }, []);
  const download = async (invId, invNum) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/customer/invoices/${invId}/pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("error");
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = `${invNum || invId}.pdf`; a.click();
      URL.revokeObjectURL(u);
      toast.success("Factura descargada");
    } catch { toast.error("No se pudo descargar"); }
  };
  if (!items) return <Loading />;
  if (!items.length) return <Empty msg="Sin facturas todavía." />;
  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-testid="invoices-tab">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left">Nº</th>
            <th className="px-3 py-2 text-left">Taller</th>
            <th className="px-3 py-2 text-left">Servicio</th>
            <th className="px-3 py-2 text-right">Total</th>
            <th className="px-3 py-2 text-right">PDF</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-t border-gray-100" data-testid={`my-invoice-${s.id}`}>
              <td className="px-3 py-2 font-mono-tab text-xs">{s.invoice_number || "—"}</td>
              <td className="px-3 py-2">{s.organization_name}</td>
              <td className="px-3 py-2 text-gray-600">{s.wash_type}</td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtEur(s.total_price)}</td>
              <td className="px-3 py-2 text-right">
                <Button size="sm" variant="outline" onClick={()=>download(s.id, s.invoice_number)}
                  className="rounded-sm h-7 px-2" data-testid={`download-invoice-${s.id}`}>
                  <DownloadSimple size={11} />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MembershipsTab() {
  const [items, setItems] = useState(null);
  useEffect(() => { customerApi.get("/customer/memberships").then(({ data }) => setItems(data)); }, []);
  if (!items) return <Loading />;
  if (!items.length) return <Empty msg="Sin membresías activas." />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="memberships-tab">
      {items.map((m) => (
        <div key={m.id} className="bg-white border border-gray-200 rounded-md p-4 relative"
          data-testid={`my-membership-${m.id}`}>
          <div className="absolute top-0 right-0 w-1.5 h-full"
            style={{ background: m.organization_brand_color || "#0033A0" }} />
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
            <Crown size={12} weight="fill" className="text-purple-600" />
            <span className="text-gray-500">{m.organization_name}</span>
          </div>
          <div className="font-display font-bold text-lg mt-1 text-[#1A1A1A]">{m.plan_name || "Membresía"}</div>
          <div className="text-xs text-gray-500 mt-2">Estado: <b className="text-green-700 uppercase">{m.status}</b></div>
          {m.stripe_subscription_id && (
            <div className="text-[10px] font-mono-tab text-[var(--ag-text-muted)] mt-1 truncate">sub: {m.stripe_subscription_id}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function ProfileTab() {
  const { account, refresh } = useCustomerAuth();
  const [form, setForm] = useState({
    name: account?.name || "", phone: account?.phone || "",
    default_address: account?.default_address || "",
    license_plates: (account?.license_plates || []).join(", "),
    language: account?.language || "es",
    marketing_consent: !!account?.marketing_consent,
  });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const plates = form.license_plates.split(",").map((s) => s.trim()).filter(Boolean);
      await customerApi.put("/customer-auth/me", {
        name: form.name, phone: form.phone,
        default_address: form.default_address,
        license_plates: plates,
        language: form.language,
        marketing_consent: form.marketing_consent,
      });
      await refresh();
      toast.success("Datos actualizados");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="bg-white border border-gray-200 rounded-md p-6 space-y-4 max-w-2xl" data-testid="profile-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider">Nombre</Label>
          <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="profile-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Teléfono</Label>
          <Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="mt-1 rounded-sm" data-testid="profile-phone" />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider">Dirección por defecto (recogidas)</Label>
        <Input value={form.default_address} onChange={(e)=>setForm({...form, default_address: e.target.value})}
          className="mt-1 rounded-sm" placeholder="Calle, número, ciudad" data-testid="profile-address" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider">Matrículas (separadas por coma)</Label>
        <Input value={form.license_plates} onChange={(e)=>setForm({...form, license_plates: e.target.value})}
          className="mt-1 rounded-sm tabular-nums uppercase" placeholder="1234ABC, 5678DEF" data-testid="profile-plates" />
        <p className="text-[10px] text-gray-500 mt-1">Los talleres detectarán automáticamente tus bonos y membresías cuando reserves.</p>
      </div>
      <label className="flex items-start gap-2 text-xs cursor-pointer">
        <input type="checkbox" checked={form.marketing_consent}
          onChange={(e)=>setForm({...form, marketing_consent: e.target.checked})}
          className="mt-0.5 accent-[#0033A0]" data-testid="profile-marketing" />
        <span className="text-gray-600">Acepto recibir ofertas y promociones de los talleres del Marketplace.</span>
      </label>
      <Button type="submit" disabled={busy} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="profile-save">
        {busy ? "Guardando…" : "Guardar cambios"}
      </Button>
    </form>
  );
}

function Loading() { return <div className="text-center text-sm text-gray-500 py-12">Cargando…</div>; }
function Empty({ msg }) { return <div className="text-center text-sm text-gray-500 py-12 bg-white border border-dashed border-gray-200 rounded-md">{msg}</div>; }

function PreferencesTab() {
  return (
    <div className="max-w-2xl space-y-4" data-testid="preferences-tab">
      <NotificationPreferencesCard apiClient={customerApi} variant="customer" />
    </div>
  );
}

function SecurityTab() {
  const { logout } = useCustomerAuth();
  return (
    <div className="max-w-2xl space-y-4" data-testid="security-tab">
      <ActiveSessionsCard
        api={customerApi}
        role="customer"
        onLogoutAll={() => {
          logout();
          setTimeout(() => { window.location.href = "/cliente/login"; }, 400);
        }}
      />
    </div>
  );
}

// =============================================================================
// Fase 2C · Customer Home NBA
// Hero contextual: nuevo cliente vs con próxima cita
// =============================================================================

function HomeTab({ account, setTab }) {
  const [state, setState] = useState({ appts: null, vouchers: null, memberships: null, invoices: null });
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [a, v, m, i] = await Promise.allSettled([
        customerApi.get("/customer/appointments"),
        customerApi.get("/customer/vouchers"),
        customerApi.get("/customer/memberships"),
        customerApi.get("/customer/invoices"),
      ]);
      setState({
        appts: a.status === "fulfilled" ? (a.value.data || []) : [],
        vouchers: v.status === "fulfilled" ? (v.value.data || []) : [],
        memberships: m.status === "fulfilled" ? (m.value.data || []) : [],
        invoices: i.status === "fulfilled" ? (i.value.data || []) : [],
      });
    })();
  }, []);

  const { appts, vouchers, memberships, invoices } = state;
  const loading = appts === null;

  const now = useMemo(() => new Date(), []);
  const upcoming = useMemo(() => {
    if (!appts) return [];
    return appts
      .filter((a) => {
        const st = (a.status || "").toLowerCase();
        if (st === "cancelado" || st === "pagado" || st === "completado") return false;
        try { return new Date(a.date) >= now; } catch { return true; }
      })
      .sort((x, y) => new Date(x.date) - new Date(y.date));
  }, [appts, now]);

  const history = useMemo(() => {
    if (!appts) return [];
    return appts
      .filter((a) => {
        const st = (a.status || "").toLowerCase();
        if (st === "pagado" || st === "completado") return true;
        try { return new Date(a.date) < now; } catch { return false; }
      })
      .sort((x, y) => new Date(y.date) - new Date(x.date))
      .slice(0, 3);
  }, [appts, now]);

  const nextAppt = upcoming[0];
  const isNewCustomer = !loading && (!appts || appts.length === 0);
  const platesCount = (account?.license_plates || []).length;

  if (loading) return <Loading />;

  return (
    <div className="space-y-6" data-testid="customer-home-tab">
      {/* Hero: nuevo cliente vs con próxima cita */}
      {isNewCustomer ? (
        <NewCustomerHero
          account={account}
          platesCount={platesCount}
          onGoMarketplace={() => navigate("/marketplace")}
          onGoVehicles={() => setTab("vehicles")}
        />
      ) : (
        <NextAppointmentHero
          appt={nextAppt}
          upcomingCount={upcoming.length}
          onGoAppointments={() => setTab("appointments")}
          onGoMarketplace={() => navigate("/marketplace")}
        />
      )}

      {/* Grid rápido · Vehículos + Cartera */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="customer-home-quick">
        <QuickCard
          icon={CarSimple}
          label="Mis vehículos"
          value={platesCount}
          hint={platesCount === 0 ? "Añade tu matrícula" : platesCount === 1 ? "1 vehículo registrado" : `${platesCount} vehículos`}
          testid="quick-vehicles"
          onClick={() => setTab("vehicles")}
        />
        <QuickCard
          icon={Wallet}
          label="Mi cartera"
          value={(vouchers?.length || 0) + (memberships?.length || 0)}
          hint={
            !vouchers?.length && !memberships?.length
              ? "Sin bonos ni membresías"
              : `${vouchers?.length || 0} bonos · ${memberships?.length || 0} membresías`
          }
          testid="quick-wallet"
          onClick={() => setTab("wallet")}
        />
      </div>

      {/* Historial reciente */}
      {history.length > 0 && (
        <section className="bg-white dark:bg-[#1A1D26] border border-gray-200 dark:border-white/10 rounded-md p-4" data-testid="customer-home-history">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock size={14} weight="duotone" className="text-[var(--ag-gold)]" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400 font-semibold">
                Servicios recientes
              </span>
            </div>
            <button
              type="button"
              onClick={() => setTab("appointments")}
              className="text-[11px] text-[var(--ag-gold)] hover:underline flex items-center gap-1"
              data-testid="home-history-more"
            >
              Ver todos <ArrowRight size={11} weight="bold" />
            </button>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-white/5">
            {history.map((a) => (
              <div key={a.id} className="py-2 flex items-center justify-between gap-2" data-testid={`home-history-${a.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate text-[#1A1A1A] dark:text-white">{a.wash_type}</div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {a.organization_name} · {fmtDate(a.date)}
                  </div>
                </div>
                <div className="text-sm font-bold tabular-nums text-[var(--ag-gold)] shrink-0">
                  {fmtEur(a.total_price)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function NewCustomerHero({ account, platesCount, onGoMarketplace, onGoVehicles }) {
  const firstName = (account?.name || "").split(" ")[0] || "Cliente";
  return (
    <section
      className="rounded-2xl p-6 sm:p-8 border transition-colors
        bg-gradient-to-br from-[#F0EEE3] via-white to-white border-gray-100
        dark:from-[#1A1D26] dark:via-[#0F1117] dark:to-[#0F1117] dark:border-white/10"
      data-testid="customer-hero-new"
    >
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#B8860B] dark:text-[#F5C76A]">
        Bienvenido a AutoGlow
      </div>
      <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1 text-[#1A1A1A] dark:text-white">
        Encuentra tu próximo servicio
      </h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 max-w-md">
        {firstName}, elige un centro cerca de ti, reserva en segundos y recibe recordatorios. Sin llamadas, sin esperas.
      </p>
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <Button
          onClick={onGoMarketplace}
          className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-[#1A1A1A] font-bold rounded-sm h-11 px-5"
          data-testid="hero-new-cta-marketplace"
        >
          <Storefront size={14} weight="fill" className="mr-1.5" /> Explorar Marketplace
        </Button>
        {platesCount === 0 && (
          <Button
            variant="outline"
            onClick={onGoVehicles}
            className="rounded-sm h-11 px-4 border-gray-200"
            data-testid="hero-new-cta-vehicle"
          >
            <CarSimple size={14} className="mr-1.5" /> Añadir mi vehículo
          </Button>
        )}
      </div>
    </section>
  );
}

function NextAppointmentHero({ appt, upcomingCount, onGoAppointments, onGoMarketplace }) {
  if (!appt) {
    // Cliente activo sin próxima cita
    return (
      <section
        className="rounded-2xl p-6 sm:p-8 border bg-gradient-to-br from-white to-[#F0EEE3] border-gray-100 dark:from-[#1A1D26] dark:to-[#0F1117] dark:border-white/10"
        data-testid="customer-hero-no-upcoming"
      >
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#B8860B] dark:text-[#F5C76A]">
          Sin citas próximas
        </div>
        <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-1 text-[#1A1A1A] dark:text-white">
          Reserva tu próximo servicio
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Cuando reserves, tu próxima cita aparecerá aquí.
        </p>
        <Button
          onClick={onGoMarketplace}
          className="mt-4 bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-[#1A1A1A] font-bold rounded-sm h-11 px-5"
          data-testid="hero-empty-cta-marketplace"
        >
          <Storefront size={14} weight="fill" className="mr-1.5" /> Ir al Marketplace
        </Button>
      </section>
    );
  }
  const brandColor = appt.organization_brand_color || "#D4AF37";
  return (
    <section
      className="rounded-2xl p-6 sm:p-8 border relative overflow-hidden bg-white dark:bg-[#1A1D26] border-gray-100 dark:border-white/10"
      data-testid="customer-hero-next-appt"
    >
      <div className="absolute top-0 left-0 h-full w-1.5" style={{ background: brandColor }} />
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#B8860B] dark:text-[#F5C76A] flex items-center gap-2">
        <CalendarBlank size={11} weight="fill" /> Próxima cita
        {upcomingCount > 1 && (
          <span className="text-gray-400 dark:text-gray-500 font-normal">· +{upcomingCount - 1} más</span>
        )}
      </div>
      <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-1 text-[#1A1A1A] dark:text-white" data-testid="hero-appt-service">
        {appt.wash_type}
      </h1>
      <div className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
        <div className="flex items-center gap-1.5">
          <Storefront size={12} className="text-gray-400" />
          <span data-testid="hero-appt-center">{appt.organization_name}</span>
          {appt.organization_city && <span className="text-gray-400">· {appt.organization_city}</span>}
        </div>
        <div className="flex items-center gap-1.5 tabular-nums" data-testid="hero-appt-date">
          <Clock size={12} className="text-gray-400" />
          <span>{fmtDate(appt.date)}</span>
        </div>
        {appt.license_plate && (
          <div className="flex items-center gap-1.5 font-mono-tab uppercase text-xs">
            <CarSimple size={12} className="text-gray-400" />
            <span>{appt.license_plate}</span>
          </div>
        )}
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button
          onClick={onGoAppointments}
          className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-[#1A1A1A] font-bold rounded-sm h-10 px-4"
          data-testid="hero-appt-cta-details"
        >
          Ver detalles <ArrowRight size={13} className="ml-1.5" weight="bold" />
        </Button>
        <Button
          variant="outline"
          onClick={onGoMarketplace}
          className="rounded-sm h-10 px-4 border-gray-200"
          data-testid="hero-appt-cta-rebook"
        >
          Reservar de nuevo
        </Button>
      </div>
    </section>
  );
}

function QuickCard({ icon: Icon, label, value, hint, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white dark:bg-[#1A1D26] border border-gray-200 dark:border-white/10 rounded-md p-4 hover:border-[var(--ag-gold)]/40 transition-colors flex items-center gap-3"
      data-testid={testid}
    >
      <div className="w-11 h-11 rounded-full bg-[var(--ag-gold)]/15 grid place-items-center text-[var(--ag-gold)] shrink-0">
        <Icon size={20} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">{label}</div>
        <div className="font-display font-black text-2xl tabular-nums text-[#1A1A1A] dark:text-white leading-none mt-1">
          {value}
        </div>
        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">{hint}</div>
      </div>
      <ArrowRight size={14} className="text-gray-400 shrink-0" />
    </button>
  );
}

// =============================================================================
// Fase 2C · Customer Vehicles Tab
// =============================================================================

function VehiclesTab() {
  const { account, refresh } = useCustomerAuth();
  const [plates, setPlates] = useState(account?.license_plates || []);
  const [input, setInput] = useState("");
  const [editIdx, setEditIdx] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setPlates(account?.license_plates || []); }, [account?.license_plates]);

  const savePlates = async (next) => {
    setBusy(true);
    try {
      await customerApi.put("/customer-auth/me", { license_plates: next });
      await refresh?.();
      setPlates(next);
      return true;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "No se pudo guardar");
      return false;
    } finally { setBusy(false); }
  };

  const normalize = (v) => (v || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");

  const addPlate = async () => {
    const v = normalize(input);
    if (!v) return;
    if (v.length < 4 || v.length > 10) return toast.error("La matrícula debe tener entre 4 y 10 caracteres");
    if (plates.includes(v)) return toast.error("Esa matrícula ya está registrada");
    const next = [...plates, v];
    if (await savePlates(next)) { setInput(""); toast.success("Vehículo añadido"); }
  };

  const removePlate = async (p) => {
    if (!window.confirm(`¿Eliminar matrícula ${p}?`)) return;
    const next = plates.filter((x) => x !== p);
    if (await savePlates(next)) toast.success("Vehículo eliminado");
  };

  const startEdit = (i, v) => { setEditIdx(i); setEditVal(v); };
  const cancelEdit = () => { setEditIdx(null); setEditVal(""); };
  const commitEdit = async () => {
    const v = normalize(editVal);
    if (!v || v.length < 4 || v.length > 10) return toast.error("Matrícula inválida");
    if (v === plates[editIdx]) return cancelEdit();
    if (plates.includes(v)) return toast.error("Esa matrícula ya está registrada");
    const next = [...plates]; next[editIdx] = v;
    if (await savePlates(next)) { toast.success("Vehículo actualizado"); cancelEdit(); }
  };

  const setAsPrimary = async (idx) => {
    if (idx === 0) return;
    const next = [...plates];
    const [chosen] = next.splice(idx, 1);
    next.unshift(chosen);
    if (await savePlates(next)) toast.success(`${chosen} · vehículo principal`);
  };

  return (
    <div className="space-y-5" data-testid="vehicles-tab">
      <div>
        <h2 className="font-display text-2xl font-black tracking-tighter text-[#1A1A1A] dark:text-white">Mis vehículos</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Registra tus matrículas para reservar más rápido y detectar automáticamente bonos y membresías.
        </p>
      </div>

      {/* Add form */}
      <form
        onSubmit={(e) => { e.preventDefault(); addPlate(); }}
        className="bg-white dark:bg-[#1A1D26] border border-gray-200 dark:border-white/10 rounded-md p-4 flex items-end gap-2 flex-wrap"
        data-testid="vehicles-add-form"
      >
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs uppercase tracking-wider">Matrícula</Label>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value.toUpperCase())}
            placeholder="1234ABC"
            maxLength={10}
            className="mt-1 font-mono-tab uppercase"
            data-testid="vehicles-add-input"
          />
        </div>
        <Button
          type="submit"
          disabled={busy || !input.trim()}
          className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-[#1A1A1A] font-bold rounded-sm h-10 px-4"
          data-testid="vehicles-add-btn"
        >
          <Plus size={13} className="mr-1" weight="bold" /> Añadir
        </Button>
      </form>

      {/* Empty state */}
      {plates.length === 0 ? (
        <div
          className="bg-white dark:bg-[#1A1D26] border border-dashed border-gray-300 dark:border-white/10 rounded-md py-10 text-center"
          data-testid="vehicles-empty"
        >
          <CarSimple size={32} weight="duotone" className="text-gray-400 mx-auto" />
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-3">
            Aún no tienes vehículos registrados.
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-1">
            Añade tu primera matrícula para agilizar las reservas.
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="vehicles-list">
          {plates.map((p, i) => (
            <div
              key={`${p}-${i}`}
              className="bg-white dark:bg-[#1A1D26] border border-gray-200 dark:border-white/10 rounded-md p-3 flex items-center gap-3"
              data-testid={`vehicle-item-${p}`}
            >
              <div className="w-11 h-11 rounded bg-[#F5F5F5] dark:bg-[#0F1117] grid place-items-center shrink-0">
                <CarSimple size={20} weight="duotone" className="text-[var(--ag-gold)]" />
              </div>
              <div className="flex-1 min-w-0">
                {editIdx === i ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editVal}
                      onChange={(e) => setEditVal(e.target.value.toUpperCase())}
                      className="font-mono-tab uppercase h-9 w-32"
                      maxLength={10}
                      autoFocus
                      data-testid={`vehicle-edit-input-${p}`}
                    />
                    <Button type="button" size="sm" onClick={commitEdit} disabled={busy}
                      className="bg-[var(--ag-gold)] text-[#1A1A1A] rounded-sm h-8 px-3" data-testid={`vehicle-save-${p}`}>
                      Guardar
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={cancelEdit}
                      className="rounded-sm h-8 px-2" data-testid={`vehicle-cancel-${p}`}>
                      <XIcon size={11} />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="font-mono-tab font-bold text-lg uppercase text-[#1A1A1A] dark:text-white">{p}</span>
                      {i === 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[var(--ag-gold)]/20 text-[#B8860B] dark:text-[#F5C76A] font-bold" data-testid={`vehicle-primary-badge-${p}`}>
                          <Star size={9} weight="fill" /> Principal
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {i === 0 ? "Se usará por defecto al reservar" : "Vehículo adicional"}
                    </div>
                  </>
                )}
              </div>
              {editIdx !== i && (
                <div className="flex items-center gap-1 shrink-0">
                  {i !== 0 && (
                    <button
                      type="button"
                      onClick={() => setAsPrimary(i)}
                      disabled={busy}
                      className="text-[11px] px-2 py-1 rounded border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:text-[var(--ag-gold)] hover:border-[var(--ag-gold)]/40"
                      data-testid={`vehicle-set-primary-${p}`}
                    >
                      <Star size={11} className="inline mr-1" /> Principal
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => startEdit(i, p)}
                    disabled={busy}
                    className="text-[11px] px-2 py-1 rounded border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:text-[var(--ag-gold)] hover:border-[var(--ag-gold)]/40"
                    data-testid={`vehicle-edit-${p}`}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => removePlate(p)}
                    disabled={busy}
                    className="text-[11px] px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
                    data-testid={`vehicle-remove-${p}`}
                  >
                    Eliminar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Fase 2C · Customer Wallet Tab
// Agrega vouchers + memberships + invoices en una sola vista
// GAP: No hay backend "wallet balance" real. La cartera unifica bonos+membresías+facturas.
// =============================================================================

function WalletTab() {
  const [state, setState] = useState({ vouchers: null, memberships: null, invoices: null });

  useEffect(() => {
    (async () => {
      const [v, m, i] = await Promise.allSettled([
        customerApi.get("/customer/vouchers"),
        customerApi.get("/customer/memberships"),
        customerApi.get("/customer/invoices"),
      ]);
      setState({
        vouchers: v.status === "fulfilled" ? (v.value.data || []) : [],
        memberships: m.status === "fulfilled" ? (m.value.data || []) : [],
        invoices: i.status === "fulfilled" ? (i.value.data || []) : [],
      });
    })();
  }, []);

  const { vouchers, memberships, invoices } = state;
  if (vouchers === null) return <Loading />;

  const totalSpent = (invoices || []).reduce((acc, s) => acc + Number(s.total_price || 0), 0);
  const activeVouchers = (vouchers || []).filter((v) => Number(v.washes_remaining || 0) > 0).length;
  const activeMemberships = (memberships || []).filter((m) => (m.status || "").toLowerCase() === "active").length;

  const downloadInvoice = async (invId, invNum) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/customer/invoices/${invId}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("error");
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = u; a.download = `${invNum || invId}.pdf`; a.click();
      URL.revokeObjectURL(u);
      toast.success("Factura descargada");
    } catch { toast.error("No se pudo descargar"); }
  };

  return (
    <div className="space-y-6" data-testid="wallet-tab">
      <div>
        <h2 className="font-display text-2xl font-black tracking-tighter text-[#1A1A1A] dark:text-white">Mi cartera</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Bonos activos, membresías y facturas de todos tus centros AutoGlow.
        </p>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="wallet-summary">
        <SummaryCard label="Bonos activos" value={activeVouchers} icon={Ticket} testid="wallet-summary-vouchers" />
        <SummaryCard label="Membresías" value={activeMemberships} icon={Crown} testid="wallet-summary-memberships" />
        <SummaryCard label="Total gastado" value={fmtEur(totalSpent)} icon={Receipt} testid="wallet-summary-spent" isMoney />
      </div>

      {/* Vouchers section */}
      <section data-testid="wallet-vouchers-section">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400 font-semibold flex items-center gap-1.5">
            <Ticket size={11} weight="fill" /> Bonos
          </div>
          <span className="text-[11px] text-gray-400 tabular-nums">{vouchers.length}</span>
        </div>
        {vouchers.length === 0 ? (
          <Empty msg="Aún no tienes bonos. Pregunta a tu taller si ofrecen packs de servicios." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {vouchers.map((v) => (
              <div key={v.id} className="bg-white dark:bg-[#1A1D26] border border-gray-200 dark:border-white/10 rounded-md p-4 relative overflow-hidden" data-testid={`wallet-voucher-${v.id}`}>
                <div className="absolute top-0 right-0 w-1.5 h-full" style={{ background: v.organization_brand_color || "#D4AF37" }} />
                <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{v.organization_name}</div>
                <div className="font-display font-bold text-lg mt-1 text-[#1A1A1A] dark:text-white">{v.voucher_name || "Bono"}</div>
                <div className="text-3xl font-black tabular-nums mt-2" style={{ color: v.organization_brand_color || "#D4AF37" }}>
                  {v.washes_remaining}
                  <span className="text-xs ml-1 text-gray-500 dark:text-gray-400 font-normal">restantes</span>
                </div>
                <div className="text-[10px] text-gray-500 dark:text-gray-500 mt-1">Caduca: {v.expires_at?.slice(0, 10) || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Memberships */}
      <section data-testid="wallet-memberships-section">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400 font-semibold flex items-center gap-1.5">
            <Crown size={11} weight="fill" /> Membresías
          </div>
          <span className="text-[11px] text-gray-400 tabular-nums">{memberships.length}</span>
        </div>
        {memberships.length === 0 ? (
          <Empty msg="Sin membresías activas." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {memberships.map((m) => (
              <div key={m.id} className="bg-white dark:bg-[#1A1D26] border border-gray-200 dark:border-white/10 rounded-md p-4 relative" data-testid={`wallet-membership-${m.id}`}>
                <div className="absolute top-0 right-0 w-1.5 h-full" style={{ background: m.organization_brand_color || "#0033A0" }} />
                <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{m.organization_name}</div>
                <div className="font-display font-bold text-lg mt-1 text-[#1A1A1A] dark:text-white">{m.plan_name || "Membresía"}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">Estado: <b className="text-green-600 dark:text-green-400 uppercase">{m.status}</b></div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Invoices */}
      <section data-testid="wallet-invoices-section">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400 font-semibold flex items-center gap-1.5">
            <Receipt size={11} weight="fill" /> Facturas
          </div>
          <span className="text-[11px] text-gray-400 tabular-nums">{invoices.length}</span>
        </div>
        {invoices.length === 0 ? (
          <Empty msg="Sin facturas todavía." />
        ) : (
          <div className="bg-white dark:bg-[#1A1D26] border border-gray-200 dark:border-white/10 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-[#0F1117] text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left">Nº</th>
                  <th className="px-3 py-2 text-left">Taller</th>
                  <th className="px-3 py-2 text-left">Servicio</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((s) => (
                  <tr key={s.id} className="border-t border-gray-100 dark:border-white/5" data-testid={`wallet-invoice-${s.id}`}>
                    <td className="px-3 py-2 font-mono-tab text-xs">{s.invoice_number || "—"}</td>
                    <td className="px-3 py-2">{s.organization_name}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{s.wash_type}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtEur(s.total_price)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => downloadInvoice(s.id, s.invoice_number)}
                        className="rounded-sm h-7 px-2" data-testid={`wallet-invoice-pdf-${s.id}`}>
                        <DownloadSimple size={11} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, isMoney, testid }) {
  return (
    <div className="bg-white dark:bg-[#1A1D26] border border-gray-200 dark:border-white/10 rounded-md p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-11 h-11 rounded-full bg-[var(--ag-gold)]/15 grid place-items-center text-[var(--ag-gold)] shrink-0">
        <Icon size={20} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">{label}</div>
        <div className={`font-display font-black tabular-nums text-[#1A1A1A] dark:text-white leading-none mt-1 ${isMoney ? "text-2xl" : "text-3xl"}`}>
          {value}
        </div>
      </div>
    </div>
  );
}
